import { z } from 'zod';

// ADMIN EXTENSIONS — plugin UI inside Dunea's own panels (Shopify admin
// blocks and actions). Each renders as a sandboxed iframe on the plugin's
// origin, opened with a short-lived session token that also carries the
// target and the resource it was opened for.
//
//   block   a card inside a resource view (an order's drawer, a product page)
//   action  a menu item on a resource that opens a modal
//
// Dunea's own panels render first-party extensions (custom fields, reviews)
// through the same targets, so a plugin card sits beside Dunea's.

export const ADMIN_TARGETS = {
  'app.dashboard': { resource: null, permission: 'app:read' },
  'shop.product.details': { resource: 'product', permission: 'shop:read' },
  'shop.product.actions': { resource: 'product', permission: 'shop:read' },
  'shop.order.details': { resource: 'order', permission: 'orders:read' },
  'shop.order.actions': { resource: 'order', permission: 'orders:read' },
  'shop.orders.bulk-actions': { resource: 'orders', permission: 'orders:read' },
  'shop.collection.details': { resource: 'collection', permission: 'shop:read' },
  'funnels.funnel.details': { resource: 'funnel', permission: 'app:read' },
  'payments.payment.details': { resource: 'payment', permission: 'payments:read' },
  'reviews.review.actions': { resource: 'review', permission: 'reviews:read' },
  'data.metaobject.details': { resource: 'metaobject', permission: 'data:read' },
} as const;

export type AdminTarget = keyof typeof ADMIN_TARGETS;
export const ADMIN_TARGET_IDS = Object.keys(ADMIN_TARGETS) as AdminTarget[];

export function adminExtensionKind(target: AdminTarget): 'block' | 'action' {
  return /actions$/.test(target) ? 'action' : 'block';
}

export const adminExtensionSchema = z.object({
  target: z.enum(ADMIN_TARGET_IDS as [AdminTarget, ...AdminTarget[]]),
  title: z.string().min(2).max(40),
  url: z
    .string()
    .url()
    .refine((u) => u.startsWith('https://'), 'must be https'),
  /** Initial iframe height for blocks (the frame can resize itself). */
  height: z.number().int().min(60).max(800).default(160),
});

export type AdminExtension = z.infer<typeof adminExtensionSchema>;

/** Extra claims in a panel session token opened for an admin extension. */
export interface AdminExtensionContext {
  target: AdminTarget;
  /** The resource id (product id, order id…); for bulk actions, ids joined by ",". */
  resourceId?: string;
}
